import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Box,
  Snackbar,
  Alert,
  InputAdornment, Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import AdminBaseLayout from '../../components/layouts/AdminBaseLayout';
import PartnerHttpService from '../../api/PartnerHttpService';
import LoadingOverlay from '../../components/LoadingOverlay';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import CNPJMask from '../../components/CNPJMask';
import TopAlert from '../../components/TopAlert';
import withAuthAdmin from '../../middleware/useWithAuthAdmin';
import FileDownloadIcon from "@mui/icons-material/FileDownload";

function AdminPartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [deletePartner, setDeletePartner] = useState(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [formData, setFormData] = useState({ 
    name: '',
    cnpj: '',
    trade_name: '',
    phone: '',
    email: '',
    contact_person: ''
  });
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const fetchPartners = async (pageNumber = 1, searchTerm = '') => {
    try {
      setLoading(true);
      const data = await PartnerHttpService.getPartners(searchTerm, pageNumber);
      setPartners(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar parceiros:', error);
      setAlert({
        open: true,
        message: 'Erro ao carregar parceiros',
        severity: 'error'
      });
      setPartners([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setPage(0); // Reset to first page when search changes
      fetchPartners(1, search);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  useEffect(() => {
    fetchPartners(page + 1, search);
  }, [page]);

  useEffect(() => {
    fetchPartners();
  }, []);

  const handleOpenDialog = (partner = null) => {
    setEditingPartner(partner);
    setFormData({ 
      name: partner ? partner.name : '',
      cnpj: partner ? partner.cnpj : '',
      trade_name: partner ? partner.trade_name : '',
      phone: partner ? partner.phone : '',
      email: partner ? partner.email : '',
      contact_person: partner ? partner.contact_person : ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPartner(null);
    setFormData({ 
      name: '',
      cnpj: '',
      trade_name: '',
      phone: '',
      email: '',
      contact_person: ''
    });
  };

  const handleOpenDeleteDialog = (partner) => {
    setDeletePartner(partner);
  };

  const handleCloseDeleteDialog = () => {
    setDeletePartner(null);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setAlert({
        open: true,
        message: 'O nome do parceiro é obrigatório',
        severity: 'error'
      });
      return;
    }

    if (!formData.cnpj.trim() || formData.cnpj.length !== 14) {
      setAlert({
        open: true,
        message: 'CNPJ inválido',
        severity: 'error'
      });
      return;
    }

    try {
      setLoading(true);
      if (editingPartner) {
        await PartnerHttpService.updatePartner(editingPartner.id, formData);
        setAlert({
          open: true,
          message: 'Parceiro atualizado com sucesso!',
          severity: 'success'
        });
      } else {
        await PartnerHttpService.createPartner(formData);
        setAlert({
          open: true,
          message: 'Parceiro criado com sucesso!',
          severity: 'success'
        });
      }
      handleCloseDialog();
      fetchPartners();
    } catch (error) {
      setAlert({
        open: true,
        message: error.message || 'Erro ao salvar parceiro',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletePartner) return;

    try {
      setLoading(true);
      await PartnerHttpService.deletePartner(deletePartner.id);
      setAlert({
        open: true,
        message: 'Parceiro excluído com sucesso!',
        severity: 'success'
      });
      handleCloseDeleteDialog();
      fetchPartners();
    } catch (error) {
      setAlert({
        open: true,
        message: 'Erro ao excluir parceiro',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCNPJ = (cnpj) => {
    if (!cnpj) return '-';
    return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
  };

  const handleCloseAlert = () => {
    setAlert({ ...alert, open: false });
  };

  if (loading) {
    return <LoadingOverlay open={true} />;
  }

  return (
    <AdminBaseLayout>
      <TopAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />

      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">
            Parceiros
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
           
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Novo Parceiro
            </Button>
            <Tooltip title="Baixar base de usuários">
              <Button
                  variant="contained"
                  color="primary"
                  startIcon={<FileDownloadIcon />}
                  component="a"
                  href={`${process.env.REACT_APP_API_BASE_URL}/report/partners/csv`}
                  target="_blank"
                  rel="noopener noreferrer"
                  disabled={loading}
              >
                Baixar base de parceiros
              </Button>
            </Tooltip>
          </Box>
        </Box>

        <TextField
          placeholder="Buscar por nome ou CNPJ"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Nome Fantasia</TableCell>
                <TableCell>CNPJ</TableCell>
                <TableCell>Telefone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Contato</TableCell>
                <TableCell>Data de Criação</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {partners.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Box sx={{ p: 2, color: 'text.secondary' }}>
                      Nenhum parceiro cadastrado.
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                partners.map((partner) => (
                  <TableRow key={partner.id}>
                    <TableCell>{partner.name}</TableCell>
                    <TableCell>{partner.trade_name}</TableCell>
                    <TableCell>{formatCNPJ(partner.cnpj)}</TableCell>
                    <TableCell>{partner.phone}</TableCell>
                    <TableCell>{partner.email}</TableCell>
                    <TableCell>{partner.contact_person}</TableCell>
                    <TableCell>{formatDate(partner.created_at)}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={() => handleOpenDialog(partner)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleOpenDeleteDialog(partner)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ 
            bgcolor: '#FF6B00', 
            color: 'white',
            fontSize: '1.5rem',
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            {editingPartner ? <EditIcon sx={{ fontSize: '2rem' }} /> : <AddIcon sx={{ fontSize: '2rem' }} />}
            {editingPartner ? 'Editar Parceiro' : 'Novo Parceiro'}
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                autoFocus
                label="Nome do Parceiro"
                type="text"
                fullWidth
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1.1rem',
                  }
                }}
              />
              <TextField
                label="Nome Fantasia"
                fullWidth
                value={formData.trade_name}
                onChange={(e) => setFormData({ ...formData, trade_name: e.target.value })}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1.1rem',
                  }
                }}
              />
              <TextField
                label="CNPJ"
                fullWidth
                value={formData.cnpj}
                onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                InputProps={{
                  inputComponent: CNPJMask,
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1.1rem',
                  }
                }}
              />
              <TextField
                label="Telefone"
                fullWidth
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1.1rem',
                  }
                }}
              />
              <TextField
                label="Email"
                fullWidth
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1.1rem',
                  }
                }}
              />
              <TextField
                label="Pessoa de Contato"
                fullWidth
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1.1rem',
                  }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={handleCloseDialog}
              variant="outlined"
              size="large"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmit}
              variant="contained"
              size="large"
              sx={{
                backgroundColor: "#FF6B00",
                '&:hover': {
                  backgroundColor: "#e65100",
                },
                minWidth: '120px'
              }}
            >
              Salvar
            </Button>
          </DialogActions>
        </Dialog>

        <DeleteConfirmationModal
          open={!!deletePartner}
          onClose={handleCloseDeleteDialog}
          onConfirm={handleDelete}
          title="Excluir Parceiro"
          message="Tem certeza que deseja excluir este parceiro? Esta ação não pode ser desfeita."
          itemName={deletePartner?.name}
        />
      </Box>
    </AdminBaseLayout>
  );
}

export default withAuthAdmin(AdminPartners);
